import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { SEOHead } from '@/components/SEOHead';
import { OptimizedImage } from '@/components/OptimizedImage';
import { DifficultyBadge } from '@/components/learn/DifficultyBadge';
import { ArrowRight, BookOpen, Clock, Target, MapPin, Star } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getAllLessons } from '@/lib/learn';

const Learn: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const lessons = getAllLessons();

  const courseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Intro to Astrophotography",
    "description": "A structured learning path from your first Milky Way shot to advanced techniques. Twelve lessons covering planning, dark skies, camera settings, focus, shooting, processing, and trip planning.",
    "url": "https://www.milkywayplanner.com/intro-to-astrophotography",
    "provider": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": "https://www.milkywayplanner.com"
    },
    "author": {
      "@type": "Person",
      "name": "Eric D. Brown, D.Sc.",
      "url": "https://ericbrown.com"
    },
    "numberOfLessons": lessons.length,
    "hasPart": lessons.map((lesson) => ({
      "@type": "Article",
      "name": lesson.title,
      "url": `https://www.milkywayplanner.com/intro-to-astrophotography/${lesson.slug}`,
      "position": lesson.order,
    })),
  };

  return (
    <>
      <SEOHead
        title="Learn Astrophotography — Milky Way Photography Guide"
        description="Twelve lessons from a first Milky Way shot through processing and trip planning: dark skies, camera settings, focus, shooting, and planning."
        keywords="learn astrophotography, milky way photography guide, astrophotography for beginners, how to photograph milky way, night sky photography tutorial"
        canonicalUrl="/intro-to-astrophotography"
        structuredData={[courseStructuredData]}
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Learn</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Intro to Astrophotography
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              A structured learning path from your first Milky Way shot to multi-night trip planning. Start at the beginning or jump to what you need.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          {/* Lesson count */}
          <div className="flex items-center gap-2 mb-8">
            <BookOpen className="w-4 h-4 text-navy" />
            <span className="text-sm text-muted-foreground">
              {lessons.length} {lessons.length === 1 ? 'lesson' : 'lessons'}
            </span>
          </div>

          {/* Lesson cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.map((lesson) => (
              <Link key={lesson.slug} to={`/intro-to-astrophotography/${lesson.slug}`}>
                <Card className="h-full hover:shadow-lg transition-shadow group overflow-hidden border-border">
                  {lesson.featured_image && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <OptimizedImage
                        src={lesson.featured_image}
                        alt={lesson.title}
                        width={384}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-navy flex items-center justify-center text-xs font-semibold text-white">
                        {lesson.order}
                      </span>
                      <DifficultyBadge difficulty={lesson.difficulty} />
                      <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
                        <Clock className="w-3 h-3" />
                        {lesson.reading_time} min
                      </span>
                    </div>

                    <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                      {lesson.title}
                    </h3>

                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 mb-3">
                      {lesson.excerpt}
                    </p>

                    <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                      Start lesson
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* CTA strip */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Start Planning</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Free Milky Way calendar with nightly visibility ratings for any location.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Open Planner <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link to="/dark-sky-locations" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Find Dark Skies</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Browse 400+ verified dark sky locations with Bortle ratings and maps.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Browse Locations <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            {(!user || !isSubscribed) ? (
              <Link to="/register" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-foreground">Go Premium</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Moon Planner, Trip Planner, PDF exports, and weather forecasts for your shoots.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                  Start Premium <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ) : (
              <Link to="/dashboard" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Your Planning Hub</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Moon Planner, Trip Planner, saved locations, and PDF exports.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Open Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Learn;
